import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User } from '@/types';
import { HttpClient } from '../helpers';
import { BackendService } from '@/types/BackendService';
import useUserStore from '@/store/useUserStore';
import { loginResponse as Data } from '@/types/loginResponse';
import { useNotificationContext } from './useNotificationContext';

const AuthContext = createContext<any>({});

export function useAuthContext() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuthContext must be used within an AuthProvider');
	}
	return context;
}

const authSessionKey = 'user_token';

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState(
		localStorage.getItem(authSessionKey)
			? JSON.parse(localStorage.getItem(authSessionKey) || '{}')
			: undefined
	);

	const { showNotification } = useNotificationContext();

	const [checkingSession, setCheckingSession] = useState(false);

	const saveSession = useCallback(
		(user: User) => {
			localStorage.setItem(authSessionKey, JSON.stringify(user));
			setUser(user);
		},
		[setUser]
	);

	const removeSession = useCallback(() => {
		localStorage.clear();
		sessionStorage.clear();
		document.cookie.split(';').forEach((c) => {
			document.cookie = c
				.replace(/^ +/, '')
				.replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
		});

		setUser(undefined);
	}, [setUser]);

	const checkSession = useCallback(
		async (token: Data) => {
			if (token?.AuthenticationResult?.AccessToken) {
				setCheckingSession(true);
				try {
					const res = await HttpClient(BackendService.Auth).get('/me');
					useUserStore.getState().setUserProfile(res.data);
				} catch (error) {
					showNotification({
						message: 'Error al verificar la sesión. Por favor, inicie sesión de nuevo.',
						type: 'error',
					});
					removeSession();
				} finally {
					setCheckingSession(false);
				}
			}
		},
		[removeSession, saveSession]
	);

	return (
		<AuthContext.Provider
			value={{
				user,
				isAuthenticated: Boolean(user),
				saveSession,
				removeSession,
				checkSession,
				checkingSession,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}
